<template>
    <div v-auto-animate>
        <div v-if="(originPlaceId && destinationPlaceId) || isClosing">
        <v-btn
            v-if="showButton"
            :data-cy="dataCyPrefix ? `${dataCyPrefix}-route-map-button` : 'route-map-button'"
            size="default"
            class="mb-2 mt-2"
            :loading="loading"
            @click.stop="toggleMap"
        >
            {{ expanded ? $t("entries.hideMap") : $t("entries.showMap") }}
        </v-btn>
        <v-expand-transition>
            <div
                v-if="expanded"
                class="route-map-wrapper mt-3 position-relative overflow-hidden rounded-lg"
            >
                <v-skeleton-loader
                    v-if="isIframeLoading"
                    type="image"
                    class="route-map-skeleton position-absolute"
                />
                <div
                    v-if="isIframeLoading"
                    class="route-map-loading-overlay position-absolute d-flex align-center justify-center font-weight-bold"
                >
                    {{ $t("entries.mapLoading") }}
                </div>
                <iframe
                    v-if="iframeUrl"
                    :data-cy="dataCyPrefix ? `${dataCyPrefix}-route-map-iframe` : 'route-map-iframe'"
                    :src="iframeUrl"
                    class="w-100 h-100 border-0"
                    :class="{ 'route-map-iframe': isIframeLoading }"
                    allowfullscreen
                    loading="lazy"
                    @load="handleIframeLoad"
                ></iframe>
            </div>
        </v-expand-transition>
        <br />
        </div>
    </div>
</template>

<script>
import { directionsEmbedUrl } from "@/api";
import { showAlertModal } from "@/utils";

export default {
    props: {
        originPlaceId: String,
        destinationPlaceId: String,
        dataCyPrefix: String,
        showButton: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            expanded: false,
            iframeUrl: null,
            loading: false,
            isClosing: false,
            isIframeLoading: false,
        };
    },
    watch: {
        originPlaceId: "refetchIfExpanded",
        destinationPlaceId: "refetchIfExpanded",
    },
    methods: {
        resetMapState() {
            this.expanded = false;
            this.iframeUrl = null;
            this.isIframeLoading = false;
        },
        showDirectionsLoadError(error) {
            showAlertModal(
                this.$store,
                this.$t("entries.directionsLoadFailed"),
                "danger",
                5000,
                error
            );
        },
        /**
         * Toggle map visibility and return the new expanded state.
         *
         * @returns {Promise<boolean>} Whether the map is expanded after toggling.
         */
        async toggleMap() {
            if (this.expanded) {
                this.resetMapState();
                return false;
            }

            this.expanded = true;
            this.isIframeLoading = true;
            this.loading = true;
            try {
                const { url } = await directionsEmbedUrl(
                    this.originPlaceId,
                    this.destinationPlaceId
                );
                this.iframeUrl = url;
                return true;
            } catch (e) {
                this.resetMapState();
                this.showDirectionsLoadError(e);
                return false;
            } finally {
                this.loading = false;
            }
        },
        async refetchIfExpanded() {
            if (!this.originPlaceId || !this.destinationPlaceId) {
                if (this.expanded) {
                    this.isClosing = true;
                    this.resetMapState();
                    setTimeout(() => {
                        this.isClosing = false;
                    }, 420);
                } else {
                    this.resetMapState();
                }
                return;
            }
            if (!this.expanded) return;

            this.loading = true;
            this.isIframeLoading = true;
            try {
                const { url } = await directionsEmbedUrl(
                    this.originPlaceId,
                    this.destinationPlaceId
                );
                this.iframeUrl = url;
            } catch (e) {
                this.resetMapState();
                this.showDirectionsLoadError(e);
            } finally {
                this.loading = false;
            }
        },
        /**
         * Mark map iframe as fully loaded.
         */
        handleIframeLoad() {
            this.isIframeLoading = false;
        },
    },
};
</script>

<style scoped>
.route-map-wrapper {
    width: 90%;
    height: 350px;
}

.route-map-skeleton {
    inset: 0;
}

.route-map-skeleton:deep(.v-skeleton-loader__image) {
    height: 100% !important;
}

.route-map-loading-overlay {
    inset: 0;
    z-index: 2;
}

.route-map-iframe {
    visibility: hidden;
}
</style>